// =============================================================================
// Shipping fact resolution — catalogue → package preparation.
//
// A single authoritative precedence for deriving package weight/dimensions from
// the product catalogue:
//
//   variant explicit value → product value → unknown (null)
//
// Nothing here fabricates a dimension. Zero, blank, and missing all resolve to
// `null` ("unknown"), and callers must surface that as missing rather than
// rating a dead-weight-only parcel.
//
// This is pure, deterministic code — no provider branching, no AI.
// =============================================================================

export interface ShippingDimensions {
  weightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
}

export type ShippingFactField = "weight" | "length" | "width" | "height";

const FIELD_LABELS: Record<ShippingFactField, string> = {
  weight: "weight",
  length: "length",
  width: "width",
  height: "height",
};

/** Human-readable label for a missing-field report (e.g. "weight"). */
export function shippingFieldLabel(field: ShippingFactField): string {
  return FIELD_LABELS[field];
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** A finite, strictly-positive number, else null. Never returns 0 as "known". */
export function positiveOrNull(value: number | null | undefined): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Resolve shipping facts with precedence variant → product → unknown.
 *
 * A variant's explicit value wins over the parent product's value. A field
 * absent on both resolves to `null` (unknown) — never a fabricated value.
 */
export function resolveShippingFacts(
  variantFacts: Partial<ShippingDimensions> | null | undefined,
  productFacts: Partial<ShippingDimensions> | null | undefined,
): ShippingDimensions {
  const pick = (v: number | null | undefined, p: number | null | undefined): number | null =>
    positiveOrNull(v) ?? positiveOrNull(p);
  return {
    weightKg: pick(variantFacts?.weightKg, productFacts?.weightKg),
    lengthCm: pick(variantFacts?.lengthCm, productFacts?.lengthCm),
    widthCm: pick(variantFacts?.widthCm, productFacts?.widthCm),
    heightCm: pick(variantFacts?.heightCm, productFacts?.heightCm),
  };
}

/** Which shipping facts are still unknown for a resolved set. */
export function missingShippingFields(facts: ShippingDimensions): ShippingFactField[] {
  const missing: ShippingFactField[] = [];
  if (facts.weightKg == null) missing.push("weight");
  if (facts.lengthCm == null) missing.push("length");
  if (facts.widthCm == null) missing.push("width");
  if (facts.heightCm == null) missing.push("height");
  return missing;
}

/** True when every shipping fact (weight + all three dimensions) is known. */
export function isCompleteShipping(facts: ShippingDimensions): boolean {
  return missingShippingFields(facts).length === 0;
}

/** Weight for a quantity of a known-weight item; null if weight or qty unknown. */
export function weightForQuantity(facts: ShippingDimensions, quantity: number): number | null {
  const w = positiveOrNull(facts.weightKg);
  if (w == null) return null;
  const qty = Math.max(0, Math.floor(Number(quantity) || 0));
  if (qty <= 0) return null;
  return round2(w * qty);
}

/** A single line to pack, with resolved facts and a quantity. */
export interface PackLine {
  facts: ShippingDimensions;
  quantity: number;
}

export interface AggregatedPackageFacts {
  weightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  /** Fields that could not be determined across all lines. */
  missing: ShippingFactField[];
}

/**
 * Aggregate several packing lines into one package's facts.
 *
 * Weight sums across lines (weight × qty). Length and width take the largest
 * single value (they do not grow per unit); height stacks (height × qty), the
 * same stacking model as the deterministic parcelization engine. Any field
 * missing on *any* line yields `null` + a `missing` entry — no partial
 * fabrication.
 */
export function aggregatePackageFacts(lines: PackLine[]): AggregatedPackageFacts {
  let weight = 0;
  let missingWeight = false;
  let length = 0;
  let missingLength = false;
  let width = 0;
  let missingWidth = false;
  let height = 0;
  let missingHeight = false;

  for (const line of lines) {
    const qty = Math.max(0, Math.floor(Number(line.quantity) || 0));
    if (qty <= 0) continue;
    const f = line.facts;

    if (f.weightKg == null) missingWeight = true;
    else weight += f.weightKg * qty;

    if (f.lengthCm == null) missingLength = true;
    else length = Math.max(length, f.lengthCm);

    if (f.widthCm == null) missingWidth = true;
    else width = Math.max(width, f.widthCm);

    if (f.heightCm == null) missingHeight = true;
    else height += f.heightCm * qty;
  }

  const missing: ShippingFactField[] = [];
  if (missingWeight) missing.push("weight");
  if (missingLength) missing.push("length");
  if (missingWidth) missing.push("width");
  if (missingHeight) missing.push("height");

  return {
    weightKg: missingWeight ? null : round2(weight),
    lengthCm: missingLength ? null : round2(length),
    widthCm: missingWidth ? null : round2(width),
    heightCm: missingHeight ? null : round2(height),
    missing,
  };
}